/**
 * Turns a pasted event brief into a filled-in event form.
 *
 * Admins currently retype every field by hand. This reads the kind of text that
 * already circulates on WhatsApp and email — a title, a date line, a venue, and
 * a list of registration questions — and produces a draft the admin reviews and
 * corrects before saving. It is deliberately a head start, not an oracle:
 * anything uncertain is reported in `warnings` rather than guessed silently.
 *
 * Dates are read Indian-first: 05/08/2026 is 5 August, not 8 May.
 */

export type FieldType =
  | 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'multiselect';

export type ValidationType = 'none' | 'email' | 'phone' | 'url' | 'number';

export interface ParsedField {
  /** Permanent id, so rewording a question never orphans answers already given. */
  fieldId: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: FieldType;
  required: boolean;
  options?: string[];
  placeholder?: string;
  validation?: ValidationType;
  /** Heading this field sits under. Absent means the form is one flat group. */
  section?: string;
}

export interface ParsedEvent {
  title?: string;
  description?: string;
  category?: string;
  startTime?: string;          // datetime-local: YYYY-MM-DDTHH:mm
  endTime?: string;
  location?: string;
  isVirtual?: boolean;
  meetingLink?: string;
  capacity?: string;
  registrationDeadline?: string;
  attendanceMode?: 'registration' | 'rsvp';
}

export interface ParsedBrief {
  event: ParsedEvent;
  fields: ParsedField[];
  /** Human-readable list of what was recognised, shown back to the admin. */
  found: string[];
  /** Things the admin still needs to fill in or check. */
  warnings: string[];
}

/* ------------------------------------------------------------------ dates */

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Four-digit year, or expand a two-digit one into the 2000s. */
function normaliseYear(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return fallback;
  return n < 100 ? 2000 + n : n;
}

interface DatePart { y: number; m: number; d: number; }

/** Where in the source string a date was matched, so we can look past it. */
interface DateHit extends DatePart { index: number; length: number; }

/**
 * Finds the first date in a string. Handles "27 August 2026", "August 27, 2026",
 * "27/08/2026" (day first) and "2026-08-27".
 */
function findDate(text: string, defaultYear: number): DatePart | null {
  const hit = findDateHit(text, defaultYear);
  return hit ? { y: hit.y, m: hit.m, d: hit.d } : null;
}

/** Returns the first date together with where it was found. */
function findDateHit(text: string, defaultYear: number): DateHit | null {
  const monthNames = Object.keys(MONTHS).join('|');

  // 27th August 2026 / 27 Aug 2026
  const dmy = new RegExp(
    `\\b(\\d{1,2})\\s*(?:st|nd|rd|th)?\\s+(${monthNames})\\b\\.?,?\\s*(\\d{2,4})?`, 'i'
  ).exec(text);
  if (dmy) {
    return {
      d: parseInt(dmy[1], 10),
      m: MONTHS[dmy[2].toLowerCase()],
      y: normaliseYear(dmy[3], defaultYear),
      index: dmy.index, length: dmy[0].length,
    };
  }

  // August 27, 2026 / Aug 27 2026
  const mdy = new RegExp(
    `\\b(${monthNames})\\b\\.?\\s+(\\d{1,2})\\s*(?:st|nd|rd|th)?,?\\s*(\\d{2,4})?`, 'i'
  ).exec(text);
  if (mdy) {
    return {
      m: MONTHS[mdy[1].toLowerCase()],
      d: parseInt(mdy[2], 10),
      y: normaliseYear(mdy[3], defaultYear),
      index: mdy.index, length: mdy[0].length,
    };
  }

  // 2026-08-27
  const iso = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(text);
  if (iso) {
    return {
      y: parseInt(iso[1], 10), m: parseInt(iso[2], 10), d: parseInt(iso[3], 10),
      index: iso.index, length: iso[0].length,
    };
  }

  // 27/08/2026 or 27-08-26 — day first, as written in India.
  const numeric = /\b(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})\b/.exec(text);
  if (numeric) {
    let d = parseInt(numeric[1], 10);
    let m = parseInt(numeric[2], 10);
    // Only swap when the first number cannot be a day.
    if (d > 12 && m <= 12) {
      // already day-first
    } else if (m > 12 && d <= 12) {
      [d, m] = [m, d];
    }
    return {
      d, m, y: normaliseYear(numeric[3], defaultYear),
      index: numeric.index, length: numeric[0].length,
    };
  }

  return null;
}

interface TimePart { h: number; min: number; }

function toTime(hRaw: string, minRaw: string | undefined, meridiem: string | undefined): TimePart {
  let h = parseInt(hRaw, 10);
  const min = minRaw ? parseInt(minRaw, 10) : 0;
  const mer = meridiem ? meridiem.toLowerCase().replace(/\./g, '') : '';
  if (mer === 'pm' && h < 12) h += 12;
  if (mer === 'am' && h === 12) h = 0;
  return { h, min };
}

/** Finds up to two times, so "10:00 AM to 1:00 PM" yields start and end. */
function findTimes(text: string): TimePart[] {
  const re = /\b(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)|\b(\d{1,2}):(\d{2})\b/gi;
  const out: TimePart[] = [];
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    if (m[1]) {
      out.push(toTime(m[1], m[2], m[3]));
    } else if (m[4]) {
      // 24-hour form with no meridiem
      out.push({ h: parseInt(m[4], 10), min: parseInt(m[5], 10) });
    }
    if (out.length === 2) break;
  }
  return out;
}

function toLocalInput(date: DatePart, time?: TimePart): string {
  const t = time || { h: 10, min: 0 };
  return `${date.y}-${pad(date.m)}-${pad(date.d)}T${pad(t.h)}:${pad(t.min)}`;
}

/* ----------------------------------------------------------------- fields */

const TYPE_HINTS: Array<[RegExp, FieldType]> = [
  [/\b(long answer|paragraph|textarea|detailed answer|multi[- ]?line)\b/i, 'textarea'],
  [/\b(dropdown|select one from|drop down)\b/i, 'select'],
  [/\b(check ?box|select all that apply|choose all|tick all|multiple choice.*multiple)\b/i, 'multiselect'],
  [/\b(radio|choose one|select one|single choice)\b/i, 'radio'],
  [/\b(short answer|text)\b/i, 'text'],
  [/\b(e-?mail)\b/i, 'email'],
  [/\b(phone|mobile|whatsapp|contact number)\b/i, 'phone'],
];

const LABEL_TYPE_RULES: Array<[RegExp, FieldType, ValidationType?]> = [
  [/e-?mail/i, 'email', 'email'],
  [/\b(phone|mobile|whatsapp|contact number|cell)\b/i, 'phone', 'phone'],
  [/\b(linkedin|github|portfolio|website|url|link|profile link)\b/i, 'text', 'url'],
  [/\b(age|years of experience|how many|number of)\b/i, 'text', 'number'],
  [/\b(address|describe|tell us|why |brief|comment|feedback|suggestion|expectation|question|message|about yourself|bio|motivation)\b/i, 'textarea'],
];

/** Short, collision-resistant id that stays with the question for its lifetime. */
function makeFieldId(): string {
  return 'q_' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);
}

/** Slug suitable for fieldName: alphanumeric, camel-ish, capped at 50 chars. */
function toFieldName(label: string, taken: Set<string>): string {
  const words = label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  let base =
    words.length === 0
      ? 'field'
      : words[0] + words.slice(1).map((w) => w[0].toUpperCase() + w.slice(1)).join('');

  base = base.slice(0, 46) || 'field';

  let name = base;
  let n = 2;
  while (taken.has(name)) {
    name = `${base}${n++}`;
  }
  taken.add(name);
  return name;
}

/** Splits "S, M, L" or "Yes / No" into option values. */
function splitOptions(raw: string): string[] {
  return raw
    .split(/\s*[,\/|]\s*|\s+or\s+/i)
    .map((s) => s.trim().replace(/^["']|["']$/g, ''))
    .filter((s) => s.length > 0 && s.length <= 100);
}

const BULLET = /^\s*(?:[-–—•●○*]|\d+[.)]|[a-hA-H][.)])\s+(.*)$/;

/** A question line, once numbering and markers are stripped. */
interface RawQuestion { label: string; required: boolean; options: string[]; hints: string[]; section?: string; }

/**
 * A line that introduces a group of questions rather than asking one.
 *
 * Recognised: "Section 1: Personal Details", "## Personal Details",
 * "**Personal Details**", "PERSONAL DETAILS", and a short trailing-colon line
 * such as "Professional background:".
 */
function readSectionHeading(line: string): string | null {
  const raw = line.trim();
  if (!raw) return null;

  // Explicitly numbered sections
  const numbered = /^(?:section|part|step)\s*\d*\s*[:.\-]\s*(.+)$/i.exec(raw);
  if (numbered) return numbered[1].trim().replace(/[:\-\s]+$/, '');

  // Markdown headings
  const hash = /^#{1,4}\s+(.+?)\s*#*$/.exec(raw);
  if (hash) return hash[1].trim();

  // Bold markers on their own line
  const bold = /^\*\*(.+?)\*\*:?$/.exec(raw);
  if (bold) return bold[1].trim();

  // A question always wins over a heading.
  if (/\?\s*$/.test(raw)) return null;
  // Required markers and inline option lists belong to questions.
  if (/\*\s*$/.test(raw) || /\([^()]*[,\/][^()]*\)\s*$/.test(raw)) return null;

  const words = raw.split(/\s+/);

  // SHOUTED HEADING
  if (
    words.length <= 6 &&
    raw.length <= 60 &&
    /[A-Z]/.test(raw) &&
    raw === raw.toUpperCase() &&
    /^[A-Z0-9\s&()'\/-]+$/.test(raw)
  ) {
    return raw
      .replace(/[:\-\s]+$/, '')
      .trim()
      .toLowerCase()
      .replace(/\b[a-z]/g, (c) => c.toUpperCase());
  }

  // A short line ending in a colon, e.g. "Professional background:"
  const colon = /^(.{2,60}):$/.exec(raw);
  if (colon && words.length <= 7 && !/^(name|email|phone|mobile)$/i.test(colon[1].trim())) {
    return colon[1].trim();
  }

  return null;
}

function cleanQuestionLine(line: string): { label: string; required: boolean; hints: string[] } {
  let s = line.trim();

  // Leading numbering: "1.", "Q2)", "3 -"
  s = s.replace(/^\s*(?:q\s*)?\d+\s*[.):-]\s*/i, '');

  let required = false;
  // Trailing required markers
  if (/\*\s*$/.test(s)) { required = true; s = s.replace(/\*\s*$/, ''); }
  const reqWord = /\((required|mandatory|compulsory)\)|\[(required|mandatory)\]|\b(required|mandatory)\b\s*$/i;
  if (reqWord.test(s)) { required = true; s = s.replace(reqWord, ''); }

  // Every trailing parenthetical is a hint, not part of the label. A line may
  // carry more than one, e.g. "Preferred location (dropdown) (Pune, Remote)".
  const hints: string[] = [];
  for (;;) {
    const paren = /\(([^()]*)\)\s*:?\s*$/.exec(s);
    if (!paren) break;
    hints.unshift(paren[1]);
    s = s.slice(0, paren.index).replace(/\s+$/, '');
  }

  s = s.replace(/[\s:?-]+$/, '').trim();
  return { label: s, required, hints };
}

function buildField(q: RawQuestion, taken: Set<string>): ParsedField {
  const hintText = q.hints.join(' ');
  const haystack = `${q.label} ${hintText}`;
  let type: FieldType | null = null;
  let validation: ValidationType | undefined;

  // An explicit hint in brackets always wins.
  outer: for (const hint of q.hints) {
    for (const [re, t] of TYPE_HINTS) {
      if (re.test(hint)) { type = t; break outer; }
    }
  }

  if (!type) {
    for (const [re, t, v] of LABEL_TYPE_RULES) {
      if (re.test(haystack)) { type = t; validation = v; break; }
    }
  }

  if (q.options.length > 0) {
    const multi = /select all|choose all|tick all|all that apply|multiple/i.test(haystack);
    // 'multiselect' renders its options as a tick list. 'checkbox' is a single
    // yes/no box and would silently discard them.
    if (multi) type = 'multiselect';
    // Keep an explicit select/radio/checkbox hint; only guess when unset or plain text.
    else if (!type || type === 'text') type = q.options.length > 4 ? 'select' : 'radio';
  }

  if (!type) type = 'text';
  if (type === 'email') validation = 'email';
  if (type === 'phone') validation = 'phone';

  const field: ParsedField = {
    fieldId: makeFieldId(),
    fieldName: toFieldName(q.label, taken),
    fieldLabel: q.label,
    fieldType: type,
    required: q.required,
  };
  if (q.section) field.section = q.section;
  if (q.options.length > 0 && ['select', 'radio', 'multiselect'].includes(type)) {
    field.options = q.options;
  }
  if (validation && validation !== 'none') field.validation = validation;
  return field;
}

/* ------------------------------------------------------------ brief parse */

const QUESTION_HEADERS =
  /^\s*(registration\s+(form|questions|details)|form\s+fields?|questions?|please\s+(provide|share|fill)|details\s+(required|needed)|fields?\s+to\s+collect|we'?ll\s+ask)\s*[:\-]?\s*$/i;

const LABELLED: Array<[string, RegExp]> = [
  ['title', /^\s*(?:event\s+)?(?:title|name)\s*[:\-]\s*(.+)$/i],
  ['date', /^\s*(?:date|when|date\s*(?:&|and)\s*time|schedule)\s*[:\-]\s*(.+)$/i],
  ['time', /^\s*(?:time|timing|timings|from)\s*[:\-]\s*(.+)$/i],
  ['venue', /^\s*(?:venue|location|where|address)\s*[:\-]\s*(.+)$/i],
  ['capacity', /^\s*(?:capacity|seats|limit|max(?:imum)?\s+(?:participants|attendees))\s*[:\-]\s*(.+)$/i],
  ['deadline', /^\s*(?:registration\s+deadline|last\s+date|rsvp\s+by|register\s+by|deadline)\s*[:\-]\s*(.+)$/i],
  ['link', /^\s*(?:link|meeting\s+link|join|zoom|meet)\s*[:\-]\s*(.+)$/i],
];

const CATEGORY_RULES: Array<[RegExp, string]> = [
  [/\bhackathon\b/i, 'hackathon'],
  [/\bwebinar\b/i, 'webinar'],
  [/\b(career\s*fair|job\s*fair|placement\s*drive)\b/i, 'career_fair'],
  [/\b(mentor(ship|ing)?|guidance)\b/i, 'mentoring'],
  [/\b(networking|meet\s*up|meetup|conclave|mixer)\b/i, 'networking'],
  [/\b(competition|contest|challenge)\b/i, 'competition'],
  [/\b(seminar|talk|lecture|conference|summit|town\s*hall)\b/i, 'seminar'],
  [/\b(workshop|bootcamp|training|masterclass)\b/i, 'workshop'],
];

const URL_RE = /(https?:\/\/[^\s<>"')]+)/i;

export function parseEventBrief(input: string, now: Date = new Date()): ParsedBrief {
  const event: ParsedEvent = {};
  const found: string[] = [];
  const warnings: string[] = [];
  const fields: ParsedField[] = [];

  const text = (input || '').replace(/\r\n?/g, '\n').trim();
  if (!text) {
    return { event, fields, found, warnings: ['Nothing was pasted.'] };
  }

  const lines = text.split('\n');
  const defaultYear = now.getFullYear();

  const labelled: Record<string, string> = {};
  const questionLines: string[] = [];
  const descriptionLines: string[] = [];
  let inQuestions = false;

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (!line.trim()) {
      if (inQuestions) questionLines.push('');
      continue;
    }

    if (QUESTION_HEADERS.test(line)) { inQuestions = true; continue; }

    if (!inQuestions) {
      let matched = false;
      for (const [key, re] of LABELLED) {
        const m = re.exec(line);
        if (m && !labelled[key]) { labelled[key] = m[1].trim(); matched = true; break; }
      }
      if (matched) continue;
      descriptionLines.push(line);
    } else {
      questionLines.push(line);
    }
  }

  // Not every brief announces its form with "Registration form:". If nothing
  // did, look for where the questions plainly start anyway: a section heading,
  // or a run of numbered lines. Only runs when the alternative is finding no
  // questions at all, so a brief that already worked is unaffected.
  if (questionLines.length === 0) {
    const isNumbered = (l: string) => /^\s*(\d+[.)]|[-–—•●○*])\s+\S/.test(l);
    let start = -1;
    for (let i = 0; i < descriptionLines.length; i++) {
      const line = descriptionLines[i];
      if (readSectionHeading(line)) { start = i; break; }
      // A lone numbered line is likely an agenda item; two in a row is a form.
      if (isNumbered(line) && isNumbered(descriptionLines[i + 1] ?? '')) { start = i; break; }
    }
    if (start >= 0) {
      questionLines.push(...descriptionLines.splice(start));
    }
  }

  /* ---- title: a labelled one, else the first meaningful line ---- */
  if (labelled.title) {
    event.title = labelled.title;
  } else {
    const candidate = descriptionLines.find((l) => l.trim().length > 2);
    if (candidate) {
      let t = candidate.trim().replace(/^#+\s*/, '');

      // A first line like "Bootcamp on 12 Oct 2026, 9am at X. 40 seats only."
      // is a whole sentence, not a title. Cut it where the details begin.
      const cut = /\s+(?:on|from)\s+\d|\s+\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)|\s+\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}/i.exec(t);
      if (cut && cut.index > 8) t = t.slice(0, cut.index);
      else if (t.length > 90) t = t.slice(0, 90).replace(/\s+\S*$/, '');

      event.title = t.replace(/[\s.,:;-]+$/, '');
      const i = descriptionLines.indexOf(candidate);
      if (i >= 0) descriptionLines[i] = candidate.trim().slice(event.title.length).replace(/^[\s.,:;-]+/, '');
      if (i >= 0 && !descriptionLines[i]) descriptionLines.splice(i, 1);
    }
  }
  if (event.title) found.push(`Title: ${event.title}`);
  else warnings.push('No title found — type one in.');

  /* ---- date and time ---- */
  const dateSource = labelled.date || descriptionLines.join(' ') || text;
  const firstHit = findDateHit(dateSource, defaultYear);
  const date = firstHit ? { y: firstHit.y, m: firstHit.m, d: firstHit.d } : null;

  // "14 November 2026 to 16 November 2026" spans several days.
  let endDate: DatePart | null = null;
  if (firstHit) {
    const rest = dateSource.slice(firstHit.index + firstHit.length);
    const second = findDateHit(rest, firstHit.y);
    if (second) {
      const a = new Date(firstHit.y, firstHit.m - 1, firstHit.d).getTime();
      const b = new Date(second.y, second.m - 1, second.d).getTime();
      if (b > a) endDate = { y: second.y, m: second.m, d: second.d };
    }
  }

  if (date) {
    // The time is often on its own line, or only in the body.
    const times =
      findTimes(labelled.time || '') .length ? findTimes(labelled.time as string)
      : findTimes(labelled.date || '').length ? findTimes(labelled.date as string)
      : findTimes(dateSource);
    event.startTime = toLocalInput(date, times[0]);
    if (endDate) {
      // Multi-day: end on the later date, at its own time if one was given.
      event.endTime = toLocalInput(endDate, times[1] || { h: 17, min: 0 });
      found.push(`Runs until ${event.endTime.split('T')[0]}`);
    } else if (times.length > 1) {
      event.endTime = toLocalInput(date, times[1]);
    } else {
      // Assume a two hour session so the form is valid; the admin can adjust.
      const start = times[0] || { h: 10, min: 0 };
      event.endTime = toLocalInput(date, { h: Math.min(23, start.h + 2), min: start.min });
      warnings.push('No end time found — a two hour slot was assumed.');
    }
    found.push(`Date: ${event.startTime.replace('T', ' at ')}`);
    if (!times.length) warnings.push('No start time found — 10:00 was assumed.');
  } else {
    warnings.push('No date found — set the start and end time by hand.');
  }

  /* ---- venue, virtual, link ---- */
  const whole = text;
  const urlMatch = URL_RE.exec(labelled.link || whole);
  const looksVirtual = /\b(online|virtual|zoom|google\s*meet|ms\s*teams|microsoft\s*teams|webinar|remote)\b/i.test(whole);

  if (labelled.venue) {
    if (/^(online|virtual|remote)\b/i.test(labelled.venue)) {
      event.isVirtual = true;
      event.location = labelled.venue.trim();
    } else {
      event.location = labelled.venue.trim();
    }
    found.push(`Venue: ${event.location}`);
  } else if (looksVirtual) {
    event.isVirtual = true;
    event.location = 'Online';
    found.push('Venue: Online');
  } else {
    warnings.push('No venue found — add a location.');
  }

  if (looksVirtual) event.isVirtual = true;
  if (event.isVirtual && urlMatch) {
    event.meetingLink = urlMatch[1];
    found.push(`Meeting link: ${event.meetingLink}`);
  } else if (event.isVirtual) {
    warnings.push('This looks like an online event but no meeting link was found.');
  }

  /* ---- capacity ---- */
  const capSource = labelled.capacity || whole;
  const capMatch =
    /(?:capacity|limited\s+to|only|first)\s*[:\-]?\s*(\d{1,5})/i.exec(capSource) ||
    /\b(\d{1,5})\s*(?:seats|spots|participants|attendees|places)\b/i.exec(capSource) ||
    (labelled.capacity ? /(\d{1,5})/.exec(labelled.capacity) : null);
  if (capMatch) {
    event.capacity = capMatch[1];
    found.push(`Capacity: ${event.capacity}`);
  }

  /* ---- registration deadline ---- */
  if (labelled.deadline) {
    const dl = findDate(labelled.deadline, defaultYear);
    if (dl) {
      const dlTimes = findTimes(labelled.deadline);
      event.registrationDeadline = toLocalInput(dl, dlTimes[0] || { h: 23, min: 59 });
      found.push(`Registration deadline: ${event.registrationDeadline.replace('T', ' at ')}`);
    }
  }

  /* ---- category: the title describes the event, the body merely mentions things ---- */
  const categoryFrom = (source: string) => {
    for (const [re, cat] of CATEGORY_RULES) if (re.test(source)) return cat;
    return null;
  };
  const category = (event.title ? categoryFrom(event.title) : null) || categoryFrom(whole);
  if (category) {
    event.category = category;
    found.push(`Category: ${category.replace('_', ' ')}`);
  }

  /* ---- questions ---- */
  const questions: RawQuestion[] = [];
  const sectionsSeen: string[] = [];
  let currentSection: string | undefined;

  for (const line of questionLines) {
    if (!line.trim()) continue;
    const bullet = BULLET.exec(line);
    const isIndented = /^\s{2,}/.test(line);

    // A bullet directly under a question is an option for it.
    if (bullet && questions.length > 0 && (isIndented || /^\s*[-–—•●○*]/.test(line))) {
      const value = bullet[1].trim();
      if (value) { questions[questions.length - 1].options.push(value); continue; }
    }

    // A heading starts a new group; it is not itself a question. Bulleted and
    // indented lines are answers or questions, never headings.
    if (!bullet && !isIndented) {
      const heading = readSectionHeading(line);
      if (heading) {
        currentSection = heading;
        if (!sectionsSeen.includes(heading)) sectionsSeen.push(heading);
        continue;
      }
    }

    const stripped = bullet ? bullet[1] : line;
    const { label, required, hints } = cleanQuestionLine(stripped);
    if (!label) continue;

    // Inline options: "T-shirt size (S, M, L, XL)". A hint that names a field
    // type is not an option list.
    let options: string[] = [];
    for (const hint of hints) {
      if (TYPE_HINTS.some(([re]) => re.test(hint))) continue;
      if (!/[,\/|]|\bor\b/i.test(hint)) continue;
      const candidate = splitOptions(hint);
      if (candidate.length >= 2) { options = candidate; break; }
    }

    questions.push({ label, required, options, hints, section: currentSection });
  }

  const taken = new Set<string>();
  for (const q of questions) fields.push(buildField(q, taken));

  if (fields.length > 0) {
    const inSections = sectionsSeen.length > 0
      ? ` across ${sectionsSeen.length} section${sectionsSeen.length === 1 ? '' : 's'}`
      : '';
    found.push(`${fields.length} registration question${fields.length === 1 ? '' : 's'}${inSections}`);
    if (sectionsSeen.length > 0) found.push(`Sections: ${sectionsSeen.join(' · ')}`);
    event.attendanceMode = 'registration';
  } else {
    // No questions at all reads like a simple invitation rather than a form.
    if (/\brsvp\b/i.test(whole)) {
      event.attendanceMode = 'rsvp';
      found.push('No questions found — set up as a quick RSVP event');
    } else {
      warnings.push('No registration questions were found.');
    }
  }

  /* ---- description: whatever is left ---- */
  const description = descriptionLines.join('\n').trim();
  if (description) {
    event.description = description;
    found.push('Description filled from the remaining text');
  } else {
    warnings.push('No description found — add one.');
  }

  return { event, fields, found, warnings };
}
